import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { authOrgSelector } from 'src/app/auth/store/auth.selectors';
import { organisationTagSelector } from '../../store/global.selectors';
import { AccessControlService } from './access-control.abstract.service';
import { AccessControl, BatchAssignPayload, BatchAssignRoleResponse, BatchRemovePayload, PaginatedAccessControl, UserRoleResponce, CreateRoleDto, UpdateRoleDto } from './access-control.model';

@Injectable()
export class AccessControlServiceImpl implements AccessControlService {
    private baseUrl: string = environment.API_GATEWAY_URL;

    constructor(
        @Inject('HTTP_CLIENT') public http: HttpClient,
        public translate: TranslateService,
        public store: Store<State>,
    ) {}

    private getResolvedOrgTag$(): Observable<string> {
        return combineLatest([this.store.select(organisationTagSelector), this.store.select(authOrgSelector)]).pipe(
            map(([globalTag, authTag]) => globalTag || authTag),
            filter((tag): tag is string => !!tag),
            take(1),
        );
    }

    public getRoles(): Observable<AccessControl[]> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.get(`${this.baseUrl}/access/admin/roles`, { headers }).pipe(map((r) => r as AccessControl[]));
            }),
        );
    }

    public getRole(id: string): Observable<AccessControl> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.get(`${this.baseUrl}/access/admin/role/${id}`, { headers }).pipe(map((response) => response as AccessControl));
            }),
        );
    }

    public getRoleUsers(id: string): Observable<UserRoleResponce> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.get(`${this.baseUrl}/access/admin/role/${id}/users?page=1&limit=100`, { headers }).pipe(map((response) => response as UserRoleResponce));
            }),
        );
    }

    public createRole(payload: CreateRoleDto): Observable<AccessControl> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.post(`${this.baseUrl}/access/admin/role`, payload, { headers }).pipe(map((response) => response as AccessControl));
            }),
        );
    }

    public updateRole(payload: UpdateRoleDto, roleId: string): Observable<AccessControl> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.patch(`${this.baseUrl}/access/admin/role/${roleId}`, payload, { headers }).pipe(map((response) => response as AccessControl));
            }),
        );
    }

    public deleteAccess(id: string): Observable<any> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.delete(`${this.baseUrl}/access/admin/role/${id}`, { headers }).pipe(map((response) => response as any));
            }),
        );
    }

    public assignRoles(payload: BatchAssignPayload): Observable<BatchAssignRoleResponse> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-application': 'admin',
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.post(`${this.baseUrl}/access/user/role/batch`, payload, { headers }).pipe(map((response) => response as BatchAssignRoleResponse));
            }),
        );
    }

    public removeRoles(payload: BatchRemovePayload): Observable<BatchAssignRoleResponse> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-application': 'admin',
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.delete(`${this.baseUrl}/access/user/role/batch`, { headers, body: payload }).pipe(map((response) => response as BatchAssignRoleResponse));
            }),
        );
    }

    public assignRole(roleId: string, userId: string): Observable<any> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) =>
                this.http
                    .patch(`${this.baseUrl}/access/user/role/${userId}/${roleId}`, null, {
                        headers: { 'x-application': 'admin', 'x-organisation': orgKey },
                    })
                    .pipe(map((user) => user as any)),
            ),
        );
    }

    public getUserRole(userId: string): Observable<AccessControl[]> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) =>
                this.http
                    .get(`${this.baseUrl}/access/user/role/${userId}`, { headers: { 'x-application': 'admin', 'x-organisation': orgKey, 'Accept-Language': this.translate.currentLang } })
                    .pipe(map((user) => user as AccessControl[])),
            ),
        );
    }

    public searchRoles(searchTerm: string, page = 1, limit = 50): Observable<PaginatedAccessControl> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => {
                const headers = new HttpHeaders({
                    'x-organisation': orgKey,
                    'Accept-Language': this.translate.currentLang,
                });
                return this.http.get(`${this.baseUrl}/access/admin/roles/search?q=${searchTerm}&page=${page}&limit=${limit}`, { headers }).pipe(map((response) => response as PaginatedAccessControl));
            }),
        );
    }
}

export const ACCESS_SERVICE_PROVIDER = {
    provide: AccessControlService,
    useClass: AccessControlServiceImpl,
};
